export type Onglet = 'inventaire' | 'courses' | 'recettes' | 'semaine'

const ONGLETS: Array<[Onglet, string, string]> = [
  ['inventaire', 'Inventaire', 'M4 7h16M4 12h16M4 17h10'],
  ['courses', 'Courses', 'M5 7h14l-1.2 11a2 2 0 0 1-2 1.8H8.2a2 2 0 0 1-2-1.8L5 7Zm3.5 0V5.5a3.5 3.5 0 0 1 7 0V7'],
  ['recettes', 'Recettes', 'M6 4h11a2 2 0 0 1 2 2v14H8a2 2 0 0 1-2-2V4Zm0 0a2 2 0 0 0-2 2v12'],
  ['semaine', 'Semaine', 'M4 7h16v13H4zM4 7V5h16v2M8 3v4M16 3v4'],
]

/**
 * Barre d'onglets basse : quatre destinations + une action.
 *
 * Le pouce d'une main droite couvre les 40 % inférieurs de l'écran ; un
 * hamburger en haut à gauche est la pire position du téléphone pour cette
 * prise. Et en PWA standalone sur iOS, il n'y a PAS de geste retour entre
 * onglets — des onglets persistants garantissent qu'on n'est jamais piégé.
 *
 * Sept onglets donneraient des créneaux de 54 px, sous le plancher de 44 px
 * et trop étroits pour « Inventaire » en français. D'où : Réglages n'est pas
 * un onglet (visité une fois par mois), Mode magasin non plus (c'est un
 * MODE), Ajout rapide non plus (ce n'est pas un lieu, c'est une action).
 *
 * Le ＋ est à DROITE, pas au centre : c'est la position de repos du pouce
 * droit. L'action la plus fréquente prend l'accès le plus facile ; la
 * destination la plus fréquente (Inventaire) n'en a pas besoin puisque c'est
 * l'écran de démarrage.
 *
 * AUCUNE transition entre onglets : ~20 ouvertures par jour × 250 ms, c'est
 * du temps réel perdu. Seul l'onglet actif change d'aspect.
 */
export function BarreOnglets({
  actif,
  onChange,
  onAjouter,
  gaucher,
}: {
  actif: Onglet
  onChange: (o: Onglet) => void
  onAjouter: () => void
  gaucher?: boolean
}) {
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-30 flex items-stretch border-t border-line bg-canvas/90 backdrop-blur-xl"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className={`flex flex-1 ${gaucher ? 'order-2' : ''}`}>
        {ONGLETS.map(([id, label, d]) => (
          <button
            key={id}
            type="button"
            onClick={() => onChange(id)}
            aria-current={actif === id ? 'page' : undefined}
            className="pressable flex h-[var(--size-bar)] flex-1 flex-col items-center justify-center gap-0.5"
          >
            <svg
              viewBox="0 0 24 24"
              className="size-6"
              fill="none"
              stroke={actif === id ? 'var(--color-accent)' : 'var(--color-muted)'}
              strokeWidth={actif === id ? 2.2 : 1.7}
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ transition: 'stroke 140ms ease, stroke-width 140ms ease' }}
            >
              <path d={d} />
            </svg>
            <span
              className="text-label normal-case"
              style={{
                color: actif === id ? 'var(--color-accent)' : 'var(--color-muted)',
                fontWeight: actif === id ? 600 : 500,
                letterSpacing: 0,
                transition: 'color 140ms ease',
              }}
            >
              {label}
            </span>
          </button>
        ))}
      </div>

      <div className={`flex items-center border-line px-2 ${gaucher ? 'order-1 border-r' : 'border-l'}`}>
        <button
          type="button"
          aria-label="Ajout rapide"
          onClick={onAjouter}
          className="pressable flex size-11 items-center justify-center rounded-full bg-accent text-accent-ink"
        >
          {/* SVG et non « ＋ » : le plus pleine chasse n'existe pas dans
              toutes les polices et rend un carré vide sur certaines. */}
          <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
      </div>
    </nav>
  )
}
